import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Check, Menu, Moon, Search, Sun } from 'lucide-react';

import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import { useUiStore } from '../../v2/theme/uiStore';
import { BackendAPI } from '../../lib/api';
import { capitalize, cn } from '../../lib/utils';

// ================================================================= //
//          Main Application Header Component                      //
// ================================================================= //
// This component provides a consistent header across the            //
// authenticated parts of the application. It includes a dynamic     //
// page title, a conditional project search bar, and user actions.   //
// ================================================================= //

/**
 * A helper function to generate a user-friendly page title from the URL pathname.
 * @param {string} pathname - The current URL pathname from React Router.
 * @returns {string} The formatted page title.
 */
const getPageTitle = (pathname: string): string => {
  if (pathname === '/') return 'Dashboard';
  if (pathname.startsWith('/project/')) return 'Project Details';
  // The /completeness route is presented as "Supplement" in the UI (the slug
  // is kept for now; rename later). Override so the banner doesn't say
  // "Completeness".
  if (pathname.startsWith('/completeness')) return 'Supplement';

  // For other top-level routes, capitalize the route name.
  const segment = pathname.split('/')[1];
  return capitalize(segment);
};

export default function Header() {
  const location = useLocation();
  const { searchQuery, setSearchQuery } = useProjectStore();

  const pageTitle = getPageTitle(location.pathname);
  const showSearchBar = location.pathname === '/';

  return (
    <header className="flex h-16 shrink-0 items-center border-b border-border bg-background px-4 md:px-6">
      {/* Mobile Menu Button (for future use) */}
      <Button variant="outline" size="icon" className="md:hidden">
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle navigation menu</span>
      </Button>

      {/* Page Title */}
      <div className="hidden md:block">
        <h1 className="text-xl font-semibold text-foreground">{pageTitle}</h1>
      </div>

      <div className="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
        {/* Project Search Bar - only shown on the dashboard */}
        <div className="ml-auto flex-1 sm:flex-initial">
          {showSearchBar && (
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 sm:w-[300px] md:w-[200px] lg:w-[300px]"
                aria-label="Search projects"
              />
            </div>
          )}
        </div>

        {/* Light/dark toggle for the classic shell (admin/rule/review). Only
            shown once the V2 interface is on — that's when the dark palette is
            wired for these screens (App.tsx toggles `.dark` on classic-shell
            routes when v2Enabled && theme==='dark'). Persists via the shared UI
            store, so it stays in sync with the V2 appearance control. B-615. */}
        <ThemeToggle />

        {/* V2 opt-in: flip the whole authed shell over to the new interface. */}
        <Button
          variant="ghost"
          size="sm"
          className="whitespace-nowrap text-muted-foreground hover:text-foreground"
          onClick={() => useUiStore.getState().enableV2()}
        >
          ✨ Try the new interface
        </Button>

        {/* Notification Button (PRP-010 — usage alerts for admins) */}
        <AlertsBell />

        {/* The user avatar and dropdown are handled in the Sidebar for a cleaner header */}
      </div>
    </header>
  );
}

// =========================================================================
// ThemeToggle — B-615
// =========================================================================
// Light/dark switch for the classic shell (admin/rule/review). Reads + writes
// the shared V2 UI store's `theme`, so it stays in sync with the V2 appearance
// control. Only rendered when V2 is enabled — that's the condition under which
// App.tsx activates the `.dark` classic palette on these routes, so the toggle
// always has a visible effect (never a dead button).
function ThemeToggle() {
  const v2Enabled = useUiStore((s) => s.v2Enabled);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  if (!v2Enabled) return null;
  const isDark = theme === 'dark';
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground hover:text-foreground"
      aria-label={label}
      title={label}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

// =========================================================================
// AlertsBell — PRP-010
// =========================================================================
// Polls /admin/alerts every 60s for admins. Shows a badge with the
// unacknowledged count. Click opens a dropdown to acknowledge or
// drill into the affected user.

interface AlertRow {
  id: string;
  threshold_id: string;
  user_id: string | null;
  user_email: string | null;
  metric: string;
  metric_value: number;
  threshold_value: number;
  action_taken: string;
  triggered_at: string;
  acknowledged_at: string | null;
}

function AlertsBell() {
  const { roles } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');

  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [unackCount, setUnackCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const refresh = () => {
    if (!isAdmin) return;
    BackendAPI.adminListAlerts({ acknowledged: false, limit: 20 })
      .then((r) => {
        setAlerts(r.alerts);
        setUnackCount(r.unacknowledged_count);
      })
      .catch(() => {
        // Silently ignore — bell shouldn't be a noisy failure point.
      });
  };

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const acknowledge = async (id: string) => {
    setBusy(true);
    try {
      await BackendAPI.adminAcknowledgeAlert(id);
      refresh();
    } catch (err: any) {
      alert(`Acknowledge failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="rounded-full text-muted-foreground hover:text-foreground"
        aria-label="View notifications"
      >
        <Bell className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative rounded-full text-muted-foreground hover:text-foreground"
        aria-label={`View notifications (${unackCount} unread)`}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unackCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unackCount > 99 ? '99+' : unackCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-lg border bg-card shadow-xl">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <h3 className="text-sm font-semibold">Usage alerts</h3>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/admin'); }}
              className="text-xs text-primary hover:underline"
            >
              View all
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                No unacknowledged alerts.
              </p>
            ) : (
              alerts.map((a) => (
                <div
                  key={a.id}
                  className="border-b px-3 py-2 last:border-b-0 hover:bg-slate-50 dark:hover:bg-muted"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                            a.action_taken === 'hard_block'
                              ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                              : a.action_taken === 'soft_block'
                                ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300'
                                : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300',
                          )}
                        >
                          {a.action_taken.replace('_', ' ')}
                        </span>
                        <span className="truncate text-sm font-medium">
                          {a.user_email || (a.user_id ? a.user_id.slice(0, 8) : 'global')}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {a.metric}:{' '}
                        <span className="font-mono">
                          {a.metric_value} / {a.threshold_value}
                        </span>
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {a.triggered_at?.slice(0, 16).replace('T', ' ')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => acknowledge(a.id)}
                      disabled={busy}
                      title="Acknowledge"
                      className="rounded p-1 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/15 disabled:opacity-40"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
